import type { IntegrationKey } from "schemas/agentTools/integrationDefinitions";
import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";

import upperFirst from "lodash-es/upperFirst";
import { knownIntegrationToolMetadataRegistry } from "schemas/agentTools/remote/integrations";

import {
  capitalizeFirstLetter,
  getRegularPastTense,
  getRegularProgressiveTense,
  getToolDisplayText,
  normalizeIdentifier,
} from "./shared";

function isIntegrationKey(
  integrationKey: string,
): integrationKey is IntegrationKey {
  return Object.hasOwn(knownIntegrationToolMetadataRegistry, integrationKey);
}

function getToolNameSuffixCandidates({ toolName }: { toolName: string }) {
  const normalizedToolName = normalizeIdentifier({ value: toolName });
  return normalizedToolName
    .split("_")
    .map((_, index, parts) => parts.slice(index).join("_"));
}

function hasIntegrationActionParticle(
  action: string,
): action is keyof typeof INTEGRATION_ACTION_PARTICLE_BY_BASE {
  return Object.hasOwn(INTEGRATION_ACTION_PARTICLE_BY_BASE, action);
}

function hasIrregularIntegrationActionTense(
  action: string,
): action is keyof typeof IRREGULAR_INTEGRATION_ACTION_TENSE_BY_BASE {
  return Object.hasOwn(IRREGULAR_INTEGRATION_ACTION_TENSE_BY_BASE, action);
}

const REGULAR_INTEGRATION_ACTIONS = new Set([
  "ask",
  "check",
  "create",
  "delete",
  "inspect",
  "list",
  "load",
  "open",
  "publish",
  "search",
  "update",
  "upload",
]);

const IRREGULAR_INTEGRATION_ACTION_TENSE_BY_BASE = {
  find: { completed: "found", progressive: "finding" },
  get: { completed: "got", progressive: "getting" },
  lookup: { completed: "looked up", progressive: "looking up" },
  read: { completed: "read", progressive: "reading" },
  run: { completed: "ran", progressive: "running" },
  show: { completed: "showed", progressive: "showing" },
  take: { completed: "took", progressive: "taking" },
  write: { completed: "wrote", progressive: "writing" },
} as const;

const INTEGRATION_ACTION_PARTICLE_BY_BASE = {
  look: "up",
  turn: "on",
} as const;

function getTensedSentenceCaseText({ text }: { text: string }) {
  const [firstWord = "", secondWord, ...remainingWords] = text
    .toLowerCase()
    .split(" ");
  const particle = hasIntegrationActionParticle(firstWord)
    ? INTEGRATION_ACTION_PARTICLE_BY_BASE[firstWord]
    : undefined;
  const isPhrasalAction = particle !== undefined && particle === secondWord;
  const irregularTense = hasIrregularIntegrationActionTense(firstWord)
    ? IRREGULAR_INTEGRATION_ACTION_TENSE_BY_BASE[firstWord]
    : undefined;
  const isKnownAction =
    irregularTense !== undefined ||
    isPhrasalAction ||
    REGULAR_INTEGRATION_ACTIONS.has(firstWord);

  if (!isKnownAction) {
    return getToolDisplayText({
      completed: "Ran",
      running: "Running",
      target: text,
    });
  }

  const completedAction =
    irregularTense?.completed ?? getRegularPastTense({ verb: firstWord });
  const progressiveAction =
    irregularTense?.progressive ??
    getRegularProgressiveTense({ verb: firstWord });
  const actionSuffix = isPhrasalAction ? ` ${particle}` : "";
  const target = (
    isPhrasalAction ? remainingWords : [secondWord, ...remainingWords]
  )
    .filter((word) => word !== undefined)
    .join(" ");

  return getToolDisplayText({
    completed: capitalizeFirstLetter({
      value: `${completedAction}${actionSuffix}`,
    }),
    pending: capitalizeFirstLetter({
      value: `${progressiveAction}${actionSuffix}`,
    }),
    running: capitalizeFirstLetter({
      value: `${progressiveAction}${actionSuffix}`,
    }),
    target,
  });
}

function sentenceCaseIdentifier({ value }: { value: string }) {
  const sentence = value.replaceAll("_", " ");
  return upperFirst(sentence);
}

interface NangoIntegrationToolMatch {
  integrationKey: IntegrationKey;
  toolName: string;
}

// NOTE (Ben, 2026-07-09, REPL-28534): The registry is static, so the
// per-integration prefix and normalized tool-name Set never change. Precompute
// them once at module load rather than rebuilding every Set on every match call
// — findNangoIntegrationToolMatch runs per tool part, per render, per streaming
// chunk across every mini-app bundle.
const normalizedIntegrationTools: {
  integrationKey: IntegrationKey;
  integrationPrefix: string;
  normalizedToolMetadataNames: Set<string>;
}[] = Object.keys(knownIntegrationToolMetadataRegistry)
  .filter(isIntegrationKey)
  .map((integrationKey) => ({
    integrationKey,
    integrationPrefix: normalizeIdentifier({ value: integrationKey }),
    normalizedToolMetadataNames: new Set(
      Object.keys(
        knownIntegrationToolMetadataRegistry[integrationKey].tools,
      ).map((toolMetadataName) => {
        return normalizeIdentifier({ value: toolMetadataName });
      }),
    ),
  }));

// NOTE (Ben, 2026-07-09, REPL-28534): The match is a pure function of the tool
// name against a static registry, so cache results by tool name.
const nangoIntegrationToolMatchCache = new Map<
  string,
  NangoIntegrationToolMatch | null
>();

export function findNangoIntegrationToolMatch({
  toolName,
}: {
  toolName: string;
}): NangoIntegrationToolMatch | null {
  const cached = nangoIntegrationToolMatchCache.get(toolName);
  if (cached !== undefined) {
    return cached;
  }

  const toolNameCandidates = getToolNameSuffixCandidates({ toolName });
  let match: NangoIntegrationToolMatch | null = null;

  outer: for (const {
    integrationKey,
    integrationPrefix,
    normalizedToolMetadataNames,
  } of normalizedIntegrationTools) {
    for (const toolNameCandidate of toolNameCandidates) {
      if (!toolNameCandidate.startsWith(`${integrationPrefix}_`)) {
        continue;
      }

      const integrationToolName = toolNameCandidate.slice(
        `${integrationPrefix}_`.length,
      );
      if (normalizedToolMetadataNames.has(integrationToolName)) {
        match = { integrationKey, toolName: integrationToolName };
        break outer;
      }
    }
  }

  nangoIntegrationToolMatchCache.set(toolName, match);
  return match;
}

export function getNangoIntegrationToolDisplay({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const nangoIntegrationToolMatch = findNangoIntegrationToolMatch({
    toolName: toolPart.tool,
  });
  const summaryText = !nangoIntegrationToolMatch
    ? sentenceCaseIdentifier({
        value: normalizeIdentifier({ value: toolPart.tool }),
      })
    : sentenceCaseIdentifier({ value: nangoIntegrationToolMatch.toolName });

  return {
    detail: null,
    ...(!nangoIntegrationToolMatch
      ? {}
      : { integrationKey: nangoIntegrationToolMatch.integrationKey }),
    summary: getTensedSentenceCaseText({ text: summaryText }),
  };
}
